<template>
  <div class="remote-locations-component">
    <div class="hr-flex hr-flex-between hr-mb-3">
      <h5 class="hr-chart-title"><i class="bx bx-map-pin"></i> Remote Work Locations</h5>
      <button class="hr-btn hr-btn-success hr-btn-sm" @click="openAddModal">
        <i class="bx bx-plus-circle"></i> Add Location
      </button>
    </div>

    <!-- Alert messages -->
    <div v-if="successMessage" class="hr-alert hr-alert-success hr-mb-3">
      <i class="bx bx-check-circle hr-me-2"></i>{{ successMessage }}
    </div>
    <div v-if="errorMessage" class="hr-alert hr-alert-danger hr-mb-3">
      <i class="bx bx-error-circle hr-me-2"></i>{{ errorMessage }}
    </div>

    <div v-if="loading" class="hr-loading-state">
      <div class="hr-spinner"></div>
      <p>Loading locations...</p>
    </div>
    <div v-else-if="locations.length === 0" class="hr-empty-state">
      <i class="bx bx-map"></i>
      <h5>No remote locations found</h5>
      <p>Add your first remote location to allow remote check-ins</p>
    </div>
    <div v-else class="hr-table-wrapper">
      <table class="hr-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Coordinates (Lat, Lng)</th>
            <th>Radius (m)</th>
            <th>Created By</th>
            <th style="text-align:center;">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="loc in locations" :key="loc.id">
            <td><strong>{{ loc.location_name }}</strong></td>
            <td>{{ loc.latitude }}, {{ loc.longitude }}</td>
            <td>{{ loc.radius_meters }}m</td>
            <td>{{ loc.created_by }}</td>
            <td style="text-align:center;">
              <div class="hr-flex hr-flex-center hr-gap-2" style="justify-content:center;">
                <button class="hr-btn hr-btn-info hr-btn-sm hr-btn-icon" @click="openEditModal(loc)" title="Edit">
                  <i class="bx bx-edit"></i>
                </button>
                <button class="hr-btn hr-btn-danger hr-btn-sm hr-btn-icon" @click="deleteLocation(loc.id)" title="Delete">
                  <i class="bx bx-trash"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Location Modal -->
    <div class="hr-modal" v-if="showModal">
      <div class="hr-modal-content" style="max-width: 500px;">
        <div class="hr-modal-header">
          <h5>{{ isEditing ? 'Edit Location' : 'Add Remote Location' }}</h5>
          <button class="hr-modal-close" @click="closeModal">&times;</button>
        </div>
        <div class="hr-modal-body">
          <div class="hr-form-group hr-mb-3">
            <label class="hr-label">Location Name <span class="required">*</span></label>
            <input type="text" v-model="form.location_name" class="hr-input" placeholder="e.g. Home Office, Branch B">
          </div>
          <div class="hr-flex hr-gap-3 hr-mb-3">
            <div class="hr-form-group" style="flex:1;">
              <label class="hr-label">Latitude</label>
              <input type="number" step="0.00000001" v-model="form.latitude" class="hr-input" placeholder="e.g. -1.2921">
            </div>
            <div class="hr-form-group" style="flex:1;">
              <label class="hr-label">Longitude</label>
              <input type="number" step="0.00000001" v-model="form.longitude" class="hr-input" placeholder="e.g. 36.8219">
            </div>
          </div>
          <div class="hr-form-group hr-mb-3">
            <label class="hr-label">Allowed Radius (meters)</label>
            <input type="number" v-model="form.radius_meters" class="hr-input">
            <small class="hr-text-muted">Set to 0 if only using for server-side auto-checkin.</small>
          </div>
        </div>
        <div class="hr-modal-footer">
          <button class="hr-btn hr-btn-secondary" @click="closeModal">Cancel</button>
          <button class="hr-btn hr-btn-primary" @click="saveLocation" :disabled="saving">
            {{ saving ? 'Saving...' : 'Save Location' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { authUrl, execute } from "@/api";

export default {
  name: "RemoteLocations",
  data() {
    return {
      locations: [],
      loading: false,
      saving: false,
      showModal: false,
      isEditing: false,
      successMessage: '',
      errorMessage: '',
      form: {
        id: null,
        location_name: '',
        latitude: '',
        longitude: '',
        radius_meters: 50
      }
    };
  },
  mounted() {
    this.fetchLocations();
  },
  methods: {
    async fetchLocations() {
      this.loading = true;
      const data = new FormData();
      data.append('function', 'getRemoteLocations');
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.locations = res.data.data.locations || [];
        }
      } catch (e) {
        this.showError('Failed to fetch remote locations');
      } finally {
        this.loading = false;
      }
    },
    openAddModal() {
      this.isEditing = false;
      this.form = { id: null, location_name: '', latitude: '', longitude: '', radius_meters: 50 };
      this.showModal = true;
    },
    openEditModal(loc) {
      this.isEditing = true;
      this.form = { ...loc };
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
    async saveLocation() {
      if (!this.form.location_name) {
        this.showError('Location name is required');
        return;
      }
      this.saving = true;
      const data = new FormData();
      data.append('function', 'manageRemoteLocation');
      data.append('id', this.form.id || '');
      data.append('location_name', this.form.location_name);
      data.append('latitude', this.form.latitude || '');
      data.append('longitude', this.form.longitude || '');
      data.append('radius_meters', this.form.radius_meters || 50);
      data.append('user_id', JSON.parse(sessionStorage.getItem('user'))?.empNo || 'System');
      
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.showSuccess(res.data.message);
          this.closeModal();
          this.fetchLocations();
        } else {
          this.showError(res.data.message);
        }
      } catch (e) {
        this.showError('Error saving location');
      } finally {
        this.saving = false;
      }
    },
    async deleteLocation(id) {
      if (!confirm('Are you sure you want to delete this remote location?')) return;
      const data = new FormData();
      data.append('function', 'deleteRemoteLocation');
      data.append('id', id);
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.showSuccess('Location deleted successfully');
          this.fetchLocations();
        } else {
          this.showError(res.data.message);
        }
      } catch (e) {
        this.showError('Error deleting location');
      }
    },
    showSuccess(msg) {
      this.successMessage = msg;
      setTimeout(() => this.successMessage = '', 3000);
    },
    showError(msg) {
      this.errorMessage = msg;
      setTimeout(() => this.errorMessage = '', 3000);
    }
  }
};
</script>
<style scoped>
.remote-locations-component {
  background: white;
  padding: 20px;
  border-radius: 8px;
}
</style>
